import { Navigate, useLocation } from 'react-router-dom'
import { hasRouteAccess, getUserRole, MODULES } from '../utils/roleAccess'
import { getToken } from '../utils/session'

/**
 * ProtectedRoute Component
 * Protects routes based on user role and authentication status
 */
const ProtectedRoute = ({ children, requireAuth = true }) => {
  const location = useLocation()
  const token = getToken()
  const userRole = getUserRole()
  
  // If authentication is required but no token, redirect to login
  if (requireAuth && !token) {
    return <Navigate to="/login" replace />
  }
  
  // Check route access based on current path
  const currentPath = location.pathname
  
  // Sensitive routes that require role check even if role is missing
  // NOTE: '/leads/new' must be protected (lead generation) even if role is missing, to prevent bypass via deep-link.
  const sensitiveRoutes = ['/credit', '/it-admin', '/leads/new']; // Credit Bureau Check, Admin, and New Lead
  
  // Always check route access for sensitive routes
  if (requireAuth && sensitiveRoutes.includes(currentPath)) {
    // Role must be present for sensitive routes (no backward-compat allowlist here)
    if (!userRole) {
      console.log(`Access denied for missing role to sensitive route "${currentPath}"`);
      return <Navigate to="/dashboard" replace />
    }

    if (!hasRouteAccess(currentPath, userRole)) {
      console.log(`Access denied for role "${userRole || 'NO_ROLE'}" to sensitive route "${currentPath}"`);
      // Redirect to dashboard if user doesn't have access
      return <Navigate to="/dashboard" replace />
    }
  }
  
  // For other routes, check access if role is provided
  if (requireAuth && userRole && !hasRouteAccess(currentPath, userRole)) {
    console.log(`Access denied for role "${userRole}" to route "${currentPath}"`);
    // If user doesn't have access to dashboard either, redirect to login
    if (currentPath === '/dashboard' || !hasRouteAccess('/dashboard', userRole)) {
      return <Navigate to="/login" replace />
    }
    // Otherwise redirect to dashboard
    return <Navigate to="/dashboard" replace />
  }
  
  // If token exists but no role, allow access for non-sensitive routes (for backward compatibility)
  // Role might not be in response structure yet
  if (requireAuth && token && !userRole && !sensitiveRoutes.includes(currentPath)) {
    console.warn("No role found but token exists - allowing access for backward compatibility");
    // Allow access but log warning
  }
  
  return children
}

export default ProtectedRoute

